import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';
import {
  selectTmActors,
  setTmActors,
  addTmActor,
  updateTmActor,
  removeTmActor,
} from '../store/tmActor/tmActorSlice.ts';
import {
  createTmActor,
  deleteTmActorById,
  listTmActors,
  updateTmActorById,
} from '../../../api.ts';
import { TmActor } from '../models/tmActor.ts';
import { selectSelectedTmAssessmentId } from '../store/tmAssessment/tmAssessmentSlice.ts';

const defaultActor: Omit<TmActor, 'id'> = {
  name: '',
};

const TmActorTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const actors = useAppSelector(selectTmActors);
  const currentOrg = useAppSelector(selectCurrentOrganization);
  const selectedAssessmentId = useAppSelector(selectSelectedTmAssessmentId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formValue, setFormValue] = useState<Omit<TmActor, 'id'>>(defaultActor);
  const [editingActor, setEditingActor] = useState<TmActor | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canEdit = currentOrg?.role && currentOrg.role !== 'viewer';
  const assessmentMissing = !selectedAssessmentId;

  const loadActors = useCallback(async () => {
    if (!selectedAssessmentId) {
      dispatch(setTmActors([]));
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await listTmActors(selectedAssessmentId);
      dispatch(setTmActors(data));
    } catch (err: any) {
      console.error('Failed to load actors', err);
      setError(err?.response?.data?.detail || 'Failed to load actors');
    } finally {
      setLoading(false);
    }
  }, [dispatch, selectedAssessmentId]);

  useEffect(() => {
    loadActors();
  }, [loadActors]);

  const openDialog = (actor?: TmActor) => {
    if (actor) {
      setEditingActor(actor);
      setFormValue({ name: actor.name });
    } else {
      setEditingActor(null);
      setFormValue(defaultActor);
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setFormValue(defaultActor);
    setEditingActor(null);
  };

  const handleSave = async () => {
    if (!selectedAssessmentId) {
      setError('Select or create an assessment first');
      return;
    }

    if (!formValue.name.trim()) {
      setError('Actor name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      if (editingActor?.id) {
        const updated = await updateTmActorById(editingActor.id, {
          name: formValue.name,
          assessment_id: selectedAssessmentId,
        });
        dispatch(updateTmActor(updated));
      } else {
        const created = await createTmActor({
          name: formValue.name,
          assessment_id: selectedAssessmentId,
        });
        dispatch(addTmActor(created));
      }
      closeDialog();
    } catch (err: any) {
      console.error('Failed to save actor', err);
      setError(err?.response?.data?.detail || 'Failed to save actor');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (actor: TmActor) => {
    if (!actor.id) return;
    if (!window.confirm(`Delete actor "${actor.name}"?`)) return;

    try {
      setLoading(true);
      setError(null);
      await deleteTmActorById(actor.id);
      dispatch(removeTmActor(actor.id));
    } catch (err: any) {
      console.error('Failed to delete actor', err);
      setError(err?.response?.data?.detail || 'Failed to delete actor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Actors</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => openDialog()}
          disabled={!canEdit || assessmentMissing}
        >
          Add Actor
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {assessmentMissing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select or create an assessment to view and edit actors.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                ID
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                Name
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {actors.map(actor => (
              <TableRow key={actor.id ?? actor.name}>
                <TableCell>{actor.id ?? '—'}</TableCell>
                <TableCell>{actor.name}</TableCell>
                <TableCell align="right">
                  <IconButton
                    aria-label="edit actor"
                    onClick={() => openDialog(actor)}
                    disabled={!canEdit}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    aria-label="delete actor"
                    onClick={() => handleDelete(actor)}
                    disabled={!canEdit}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {actors.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  {assessmentMissing
                    ? 'Select an assessment to view actors'
                    : loading
                      ? 'Loading actors...'
                      : 'No actors found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingActor ? 'Edit Actor' : 'Add Actor'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Name"
            value={formValue.name}
            onChange={e => setFormValue({ name: e.target.value })}
            fullWidth
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading || !canEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TmActorTable;
